import { useQueryClient } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BottomSheet } from "react-spring-bottom-sheet";

import { IHospitalConfiguration } from "../../../../@types/Login";
import { IPatientInfo } from "../../../../@types/Patient";
import { IPatientClinicianForm } from "../../../../@types/Proms";
import { ChevronLeft } from "../../../../assets/icons/ChevronLeft";
import { ChevronRight } from "../../../../assets/icons/ChevronRight";
import { SkeletonLoading } from "../../../../components/Skeleton";
import { ToastNotification } from "../../../../components/ToastNotification";
import {
  AUTH_HOSPITAL_CONFIGURATION,
  PATIENT_LIST_SORTING_STATE,
} from "../../../../constants/localStorageKeys";
import { getPatientListSortParams } from "../../../../constants/patientListSort";
import { patientListVariants } from "../../../../constants/patientListVariants";
import { useFlagPatient } from "../../../../hooks/mutations/patients";
import {
  usePatientInfo,
  usePatientMonitoringInfo,
  usePatientNavigationIds,
} from "../../../../hooks/queries/patients";
import { useApplicableRagMeasurements } from "../../../../hooks/queries/ragConfig";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { promsService } from "../../../../services/promsService";
import { css } from "../../../../styles";
import { isUat } from "../../../../utils/environment";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { getStoredPatientListVariant } from "../../../../utils/patientListVariant";
import { safeJsonParse, safeStorage } from "../../../../utils/storage";
import { FlagPatientButton } from "../../../PatientList/components/PatientListSortedTable/components/FlagPatientButton/FlagPatientButton";
import { AddFormModal } from "../AddFormModal";
import { DownloadPatientData } from "../DownloadPatientData";
import {
  Title,
  Container,
  DesktopContainer,
  TitleContainer,
  PatientCard,
  MobileTitleContainer,
  SubNavButton,
  RightHeadingContainer,
  PatientNavigationContainer,
  tooltipStyles,
  ChevronButton,
  ISHLTBaselineChip,
  SecondaryTitle,
} from "./PatientSubNav.styles";

dayjs.extend(relativeTime);

type PatientSubNavProps = {
  isLateralPatientMenuOpenOnDesktop: boolean;
  ishltFev1Baseline?: number;
  onISHLTFev1ChipClick?: () => void;
};

const bottomSheetCss = css({
  zIndex: "1000px",
});

export function PatientSubNav({
  isLateralPatientMenuOpenOnDesktop,
  ishltFev1Baseline,
  onISHLTFev1ChipClick,
}: PatientSubNavProps) {
  const { t } = useTranslation();

  const { isMobile } = useIsMobile();
  const { openModal, closeModal } = useModal();
  const [showNotification, setShowNotification] = useState(false);
  const [format, setFormat] = useState("");
  const [patientCards, setPatientCards] = useState<
    Array<{ name: string; description: string }>
  >([]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const mutate = useFlagPatient();

  useEffect(() => {
    setIsMenuOpen(isLateralPatientMenuOpenOnDesktop);
  }, [isLateralPatientMenuOpenOnDesktop]);

  const navigate = useNavigate();
  const { patientId } = useParams();
  const location = useLocation();

  const queryClient = useQueryClient();

  const { patient, isFetching } = usePatientInfo({ patientId });
  const { patientMonitoringInfo } = usePatientMonitoringInfo({ patientId });
  const isLoading = isFetching && !patient?.id;
  const [previousPatientId, setPreviousPatientId] = useState<number | null>(
    null
  );
  const [nextPatientId, setNextPatientId] = useState<number | null>(null);
  const [variant, setVariant] = useState("spirometry");
  const [clinicianForms, setClinicanForms] = useState<IPatientClinicianForm[]>(
    []
  );

  // Read the list variant + sort the user last saw so navigation IDs match the
  // same filter and order. The list table is server-paginated, so it only knows
  // the current page — we fetch the full ordered set from the backend instead.
  const storedVariant = getStoredPatientListVariant();

  const storedSorting = safeJsonParse<SortingState>(
    sessionStorage.getItem(PATIENT_LIST_SORTING_STATE),
    []
  );
  // EN-5123: the RAG columns are dynamic, so their column id (a measurement code) has to be
  // recognised as a valid sort key here too, or the arrows would traverse a different order than the
  // table. The applicable set is a superset of what the user has enabled; the backend falls back to
  // its default order for a measurement that isn't one of their columns.
  const { applicableMeasurements } = useApplicableRagMeasurements();
  const { sortBy, sortDir } = getPatientListSortParams(
    storedSorting,
    applicableMeasurements
  );

  const { patientIds: navigationIds } = usePatientNavigationIds({
    variant: storedVariant,
    sortBy,
    sortDir,
  });

  useEffect(() => {
    if (navigationIds && navigationIds.length > 0) {
      const currentPatientIndex = navigationIds.indexOf(Number(patientId));

      if (currentPatientIndex > 0) {
        setPreviousPatientId(navigationIds[currentPatientIndex - 1]);
      } else {
        setPreviousPatientId(null);
      }

      if (currentPatientIndex < navigationIds.length - 1) {
        setNextPatientId(navigationIds[currentPatientIndex + 1]);
      } else {
        setNextPatientId(null);
      }
    }

    if (location.pathname.includes(patientListVariants.ALERTS)) {
      setVariant("alerts");
    }
  }, [navigationIds, patientId, location.pathname]);

  useEffect(() => {
    // Reset forms when patient changes to prevent stale data showing
    setClinicanForms([]);

    if (!patient?.id) return;

    (async () => {
      const { data } = await promsService.getPatientClinicanForms(
        patient.id.toString()
      );

      setClinicanForms(data?.result ?? []);
    })();
  }, [patient?.id]);

  useEffect(() => {
    if (!patient && !patientMonitoringInfo) return;

    setPatientCards([
      {
        name: t("patientPage_subNav_hospitalId"),
        description: patient?.hospitalId || "-",
      },
      {
        name: t("patientPage_subNav_email"),
        description: patient?.user?.email || "-",
      },
      {
        name: t("patientPage_subNav_lastReview"),
        description: patientMonitoringInfo?.latestReview || "-",
      },
      {
        name: t("patientPage_subNav_lastReviewedBy"),
        description: patientMonitoringInfo?.latestReviewedBy || "-",
      },
      {
        name: t("patientPage_subNav_phone"),
        description: patient?.user?.phoneNumber || "-",
      },
      {
        name: t("patientPage_subNav_hospital"),
        description: patientMonitoringInfo?.hospitalName || "-",
      },
      {
        name: t("patientPage_subNav_lastUsed"),
        description: patient?.user?.lastUsed || "-",
      },
    ]);
  }, [patient, patientMonitoringInfo]);

  const patientBirthDate = patient?.birthDate ? dayjs(patient.birthDate) : null;
  const patientAge = patientBirthDate
    ? dayjs().diff(patientBirthDate, "year")
    : null;

  const hospitalConfig = safeJsonParse<IHospitalConfiguration | undefined>(
    safeStorage.getItem(AUTH_HOSPITAL_CONFIGURATION),
    undefined
  );
  const isHseHeaderEnabled = Boolean(hospitalConfig?.enableHseHeader);

  const hospitalPatientId = patient?.hospitalPatientId;
  const phoneNumber = patient?.user?.phoneNumber;

  const handleFlagPatient = async (patientId: number, flagged: boolean) => {
    await mutate({
      patientId,
      flagged,
    });

    queryClient.setQueryData(
      [`patient-info-${patientId}`, patientId.toString()],
      (patientInfo?: IPatientInfo) => {
        if (patientInfo) {
          return { ...patientInfo, flagged };
        }
        return patientInfo;
      }
    );
  };

  const handleModalClose = (success?: boolean, format?: string) => {
    closeModal();

    if (success && format) {
      setFormat(format);
      setShowNotification(true);

      setTimeout(() => {
        setShowNotification(false);
      }, 4000);
    }
  };

  const handleDownloadReport = () => {
    openModal(
      <DownloadPatientData patient={patient} onClose={handleModalClose} />,
      {
        width: "100%",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  const handleAddForm = () => {
    if (!patientId) return;

    mixpanelActions.track("User Action: Add Form (banner)");

    openModal(
      <AddFormModal
        patientId={patientId}
        clinicianForms={clinicianForms}
        onClose={handleModalClose}
      />,
      {
        width: "100%",
        height: "100%",
        showCloseButton: true,
      }
    );
  };

  const handlePreviousPatient = () => {
    navigate(`/patients/${previousPatientId}/${variant}`);
  };

  const handleNextPatient = () => {
    navigate(`/patients/${nextPatientId}/${variant}`);
  };

  if (isMobile) {
    const mobileTopHeaderSize = 69;
    const mobileBottomHeaderSize = 87;

    // Mobile skeleton
    if (isLoading) {
      return (
        <BottomSheet
          open
          data-body-scroll-lock-ignore="true"
          blocking={false}
          skipInitialTransition
          scrollLocking={false}
          className={bottomSheetCss()}
          defaultSnap={() => mobileBottomHeaderSize}
          snapPoints={({ maxHeight }) => [
            mobileBottomHeaderSize,
            maxHeight - mobileTopHeaderSize,
          ]}
          header={
            <MobileTitleContainer>
              <SkeletonLoading width={140} height={19} />
              <span style={{ color: "#666666" }}>•</span>
              <SkeletonLoading width={40} height={19} />
            </MobileTitleContainer>
          }
        >
          {Array.from({ length: 7 }).map((_, index) => (
            <PatientCard key={`skeleton-card-${index}`}>
              <SkeletonLoading width={80} height={16} />
              <SkeletonLoading width={120} height={16} />
            </PatientCard>
          ))}
        </BottomSheet>
      );
    }

    return (
      <BottomSheet
        open
        data-body-scroll-lock-ignore="true"
        blocking={false}
        skipInitialTransition
        scrollLocking={false}
        className={bottomSheetCss()}
        defaultSnap={() => mobileBottomHeaderSize}
        snapPoints={({ maxHeight }) => [
          mobileBottomHeaderSize,
          maxHeight - mobileTopHeaderSize,
        ]}
        header={
          <MobileTitleContainer>
            <Title>{patient.user?.name}</Title>
            <span style={{ color: "#666666" }}>•</span>
            <Title>{patient?.birthDate && patientAge}</Title>
            <Title>{patient?.gender && patient.gender.toUpperCase()}</Title>
          </MobileTitleContainer>
        }
      >
        {patientCards.map((card, index) => (
          <PatientCard key={`${card.description}_${index}`}>
            <p>
              <strong>{card.name}</strong>
            </p>
            <p>{card.description}</p>
          </PatientCard>
        ))}
        {showNotification ? (
          <ToastNotification
            message={t("patientPage_toasts_reportDownloaded")}
            type="success"
          />
        ) : null}
      </BottomSheet>
    );
  }

  // Desktop skeleton
  if (isLoading) {
    return (
      <DesktopContainer isLateralPatientMenuOpenOnDesktop={isMenuOpen}>
        <Container>
          <TitleContainer>
            <SkeletonLoading width={180} height={22} />
            <span>•</span>
            <SkeletonLoading width={40} height={22} />
          </TitleContainer>
          <RightHeadingContainer>
            <SkeletonLoading width={40} height={26} borderRadius="8px" />
            <SkeletonLoading width={91} height={26} borderRadius="8px" />
            <SkeletonLoading width={91} height={26} borderRadius="8px" />
            <PatientNavigationContainer>
              <SkeletonLoading width={26} height={26} borderRadius="50%" />
              <SkeletonLoading width={26} height={26} borderRadius="50%" />
            </PatientNavigationContainer>
          </RightHeadingContainer>
        </Container>
      </DesktopContainer>
    );
  }

  return (
    <DesktopContainer isLateralPatientMenuOpenOnDesktop={isMenuOpen}>
      <Container>
        <TitleContainer>
          <Title>{patient.user?.name}</Title>
          <span>•</span>
          <Title>{patient?.birthDate && patientAge}</Title>
          <Title>{patient?.gender && patient.gender.toUpperCase()}</Title>

          {isHseHeaderEnabled && hospitalPatientId ? (
            <>
              <span>•</span>
              <SecondaryTitle>{hospitalPatientId}</SecondaryTitle>
            </>
          ) : null}

          {isHseHeaderEnabled && patient?.ihi ? (
            <>
              <span>•</span>
              <SecondaryTitle>{patient.ihi}</SecondaryTitle>
            </>
          ) : null}

          {isHseHeaderEnabled && phoneNumber ? (
            <>
              <span>•</span>
              <SecondaryTitle>{phoneNumber}</SecondaryTitle>
            </>
          ) : null}

          {ishltFev1Baseline ? (
            <ISHLTBaselineChip onClick={onISHLTFev1ChipClick}>
              {t("patientPage_subNav_ishltBaseline", {
                value: ishltFev1Baseline.toFixed(2),
              })}
            </ISHLTBaselineChip>
          ) : null}
        </TitleContainer>
        <RightHeadingContainer>
          <Tooltip
            title={
              patient.flagged
                ? t("patientPage_subNav_removeFlag")
                : t("patientPage_subNav_flagPatient")
            }
            zIndex={3002}
            color="#4B4B4B"
            arrow={false}
            placement="bottomLeft"
            styles={{
              body: tooltipStyles,
            }}
          >
            <div>
              <FlagPatientButton
                onClick={async (e) => {
                  e.stopPropagation();

                  await handleFlagPatient(patient.id, !patient.flagged);
                }}
                flagged={patient.flagged}
                isPatientsPage
              />
            </div>
          </Tooltip>
          {clinicianForms.length > 0 && (
            <SubNavButton isGray onClick={handleAddForm}>
              {t("patientPage_forms_add")}
            </SubNavButton>
          )}
          {isUat() && (
            <SubNavButton isLaunch>
              {t("patientPage_subNav_launchNscr")}
            </SubNavButton>
          )}
          <SubNavButton onClick={handleDownloadReport}>
            {t("patientPage_subNav_download")}
          </SubNavButton>
          <PatientNavigationContainer>
            {previousPatientId !== null ? (
              <Tooltip
                title={t("patientPage_subNav_previousPatient")}
                zIndex={3002}
                color="#4B4B4B"
                arrow={false}
                placement="bottomLeft"
                align={{ offset: [-90, 5] }}
                styles={{
                  body: tooltipStyles,
                }}
              >
                <ChevronButton onClick={handlePreviousPatient}>
                  <ChevronLeft opacity="1.0" />
                </ChevronButton>
              </Tooltip>
            ) : (
              <ChevronButton inactive disabled aria-disabled>
                <ChevronLeft opacity="0.38" />
              </ChevronButton>
            )}
            {nextPatientId !== null ? (
              <Tooltip
                title={t("patientPage_subNav_nextPatient")}
                zIndex={3002}
                color="#4B4B4B"
                arrow={false}
                placement="bottomLeft"
                styles={{
                  body: tooltipStyles,
                }}
              >
                <ChevronButton onClick={handleNextPatient}>
                  <ChevronRight opacity="1.0" />
                </ChevronButton>
              </Tooltip>
            ) : (
              <ChevronButton inactive disabled aria-disabled>
                <ChevronRight opacity="0.38" />
              </ChevronButton>
            )}
          </PatientNavigationContainer>
        </RightHeadingContainer>
        {showNotification ? (
          <ToastNotification
            message={t("patientPage_toasts_formatDownloaded", { format })}
            type="success"
          />
        ) : null}
      </Container>
    </DesktopContainer>
  );
}
